import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Select } from 'antd';
import { useNavigate } from 'react-router-dom';

import { api } from '../../api/client';
import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import { getCollectionFavoriteCardIds, getCollectionMemberCardIds } from '../../utils/collections';
import { accessContextFor, cardHomeOf, subCollectionsOf } from '../../utils/collectionFamily';
import { API_COLLECTIONS_URL } from '../../utils';
import { normalizeId } from '../../utils/ids';
import { IMPORT_LABEL, importPath } from '../../utils/importEntry';
import { UPLOAD } from '../icons';
import {
	CardOptionLabel,
	CardPickerOptionRow,
	buildMemberCardOptions,
} from '../cards/CardVisuals';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { useOptionalFavorites } from '../../contexts/FavoritesContext';
import useQuickAddTemplates from '../../hooks/useQuickAddTemplates';
import useMultiAddForms from '../../hooks/useMultiAddForms';
import { canCreateItems, canWriteCard } from '../../utils/permissions';
import { optionSearchFilter } from '../Input';
import FavoriteCardButtons, { newItemPath, selectFavoriteCards } from './FavoriteCardButtons';
import QuickAddButtons, { launchQuickAdd, selectQuickAddTemplates } from './QuickAddButtons';
import AddItemManagerModal from './AddItemManagerModal';
import MultiAddButtons, { launchMultiAdd, selectMultiAddForms } from './MultiAddButtons';
import { isBorrowedCard } from '../../utils/linkedRecords';

/**
 * "Add item" trigger: opens a modal that leads with the collection's identity,
 * then offers three paths to a new item —
 *
 *   Card       the exhaustive path: every writable card of the collection in a
 *              searchable select; pick one, confirm with "Add item".
 *   Favorites  the fast path: the collection's and the user's favorite cards
 *              as one-click launchers (FavoriteCardButtons → LaunchButtonRow).
 *   Quick add  the templated path: the collection's and the user's quick add
 *              templates as launchers (QuickAddButtons), landing on the form
 *              PRE-FILLED; "Manage…" opens the quick add AND multi add lists
 *              for writers (the one door to creating either).
 *   Multi add  the several-items path: the collection's and the user's multi
 *              add forms as launchers (MultiAddButtons), landing on the
 *              form's own page — one form, several items of several cards.
 *
 * The first two land on the blank new-item form for the card. The select keeps its
 * star toggle so a user can favorite a card without leaving; the favorites
 * concept otherwise lives in the launch row (no "Favorites" option group, no
 * tier glyphs in the rows).
 *
 * Card definitions come from the shared CardDefinitionsContext; only the
 * collection record (for its card refs and access) is fetched here, on open.
 */
export default function AddItemButton({
	collectionId,
	// Optional: callers that already hold the collection record pass it so the
	// modal's identity badge paints immediately instead of after the fetch.
	collection = null,
	buttonText = 'Add item',
	children,
	buttonProps = {},
	// Controlled use, for a door that is not this button — the command
	// palette's "Add item to {personal collection}": pass `open` and
	// `onOpenChange`, and `hideTrigger` to render the dialog alone.
	open: openProp,
	onOpenChange,
	hideTrigger = false,
}) {
	const controlled = openProp !== undefined;
	const [openState, setOpenState] = useState(false);
	const open = controlled ? Boolean(openProp) : openState;
	const setOpen = useCallback(next => {
		if (!controlled) setOpenState(next);
		if (typeof onOpenChange === 'function') onOpenChange(next);
	}, [controlled, onOpenChange]);
	const [collectionCardIds, setCollectionCardIds] = useState(null);
	const [fetchedCollection, setFetchedCollection] = useState(null);
	const [loadingCollection, setLoadingCollection] = useState(false);
	const [collectionError, setCollectionError] = useState(false);
	const [selectedCardId, setSelectedCardId] = useState();
	const [managerOpen, setManagerOpen] = useState(false);
	const navigate = useNavigate();
	const { cards, cardsById, loading: cardsLoading } = useCardDefinitions();
	const favorites = useOptionalFavorites();
	// Fetched only once the modal opens: the trigger sits in the context bar
	// on every collection route and must cost nothing until clicked.
	const quickAdds = useQuickAddTemplates(collectionId, { enabled: open });
	const multiAdds = useMultiAddForms(collectionId, { enabled: open });

	// The trigger outlives a collection switch (the context bar keeps
	// CollectionActions mounted from one collection to the next), so the
	// previous collection's fetched record — and a card picked from it — must
	// not paint the identity, gate the cards, or enable "Add item" here.
	useEffect(() => {
		setFetchedCollection(null);
		setCollectionError(false);
		setCollectionCardIds(null);
		setSelectedCardId(undefined);
	}, [collectionId]);

	const fetchCollectionCardIds = async () => {
		if (!collectionId) return;
		setLoadingCollection(true);
		setCollectionError(false);
		try {
			const { data } = await api.get(`${API_COLLECTIONS_URL}/${collectionId}`);
			setFetchedCollection(data || null);
			const refs = Array.isArray(data?.cards) ? data.cards : [];
			setCollectionCardIds(new Set(refs.map(ref => normalizeId(ref?.cardDefinitionId)).filter(Boolean)));
		} catch (e) {
			// A failed read is its own state with Retry, never an empty card
			// set: the picker would look like a collection with no cards.
			setCollectionError(true);
		} finally {
			setLoadingCollection(false);
		}
	};

	// Two-source membership via the shared helper: embedded refs plus cards
	// OWNED by this collection. Stays null until the fetch lands (the loading
	// signal) and on a failed fetch (the error state), and falls back to the
	// embedded-only set when there is no fetched record to union from.
	const effectiveCardIds = useMemo(() => {
		if (!collectionCardIds) return null;
		if (!fetchedCollection) return collectionCardIds;
		return getCollectionMemberCardIds(fetchedCollection, cards);
	}, [collectionCardIds, fetchedCollection, cards]);

	// The fetched record is the fresher of the two (it carries myAccess and the
	// manager's pins), so it wins over a prop handed in by the caller.
	const accessSource = fetchedCollection || collection;

	const cardOptions = useMemo(() => {
		if (!effectiveCardIds) return [];
		// Only cards the caller may WRITE: a card-scoped writer must not pick a
		// read-only (or ungranted) card and click into a 403.
		// Never a card a linked collection LENDS this one (keepr-api § Merged
		// lists): its records are created where it lives — the API answers
		// 403 `card_borrowed` — and membership already leaves it out; this
		// holds even if a borrowed card ever reaches the member set.
		const own = buildMemberCardOptions(cards || [], effectiveCardIds)
			.filter(opt => !isBorrowedCard(opt.card))
			.filter(opt => !accessSource || canWriteCard(accessSource, opt.value))
			.map(opt => ({ ...opt, label: <CardOptionLabel card={opt.card} depth={opt.depth} /> }));
		// A PARENT collection (docs/plans/sub-collections.md §5.4): its own
		// cards, then one group per sub-collection — headed by its badge —
		// of that sub-collection's cards the caller can write there. Such a
		// card's item is made in the sub-collection.
		const subs = subCollectionsOf(fetchedCollection);
		if (!subs.length) return own;
		const groups = subs.map(sub => {
			const ids = new Set([...sub.cardIds].filter(id => !effectiveCardIds.has(id)));
			const options = buildMemberCardOptions(cards || [], ids)
				.filter(opt => canWriteCard(accessContextFor(fetchedCollection, opt.value), opt.value))
				.map(opt => ({
					...opt,
					searchText: `${opt.searchText || ''} ${sub.name}`.trim(),
					label: <CardOptionLabel card={opt.card} depth={opt.depth} />,
				}));
			return options.length ? {
				key: `sub:${sub._id}`,
				title: `In ${sub.name}`,
				label: <RecordBadge kind="collection" record={sub} title={sub.name} size="xs" />,
				options,
			} : null;
		}).filter(Boolean);
		if (!groups.length) return own;
		return [
			...(own.length ? [{ key: 'own', title: `In ${fetchedCollection?.name || 'this collection'}`, label: `In ${fetchedCollection?.name || 'this collection'}`, options: own }] : []),
			...groups,
		];
	}, [cards, effectiveCardIds, accessSource, fetchedCollection]);

	// Where the picked card's item is made: the sub-collection that holds it,
	// or this collection.
	const selectedHome = useMemo(
		() => (selectedCardId && fetchedCollection ? cardHomeOf(selectedCardId, fetchedCollection) : null),
		[selectedCardId, fetchedCollection]
	);

	// Cards the collection's manager pinned for everyone.
	const collectionFavoriteIds = useMemo(
		() => getCollectionFavoriteCardIds(accessSource),
		[accessSource]
	);

	const isFavoriteCard = useCallback(
		cardId => !!favorites && favorites.isFavoriteCard(collectionId, cardId),
		[favorites, collectionId]
	);

	// The launchers wait for the fetched record: it is the copy that says which
	// cards the caller may write and which ones the manager pinned. Whether
	// there ARE any decides whether the Favorites section shows at all.
	const favoriteCards = useMemo(
		() => (fetchedCollection ? selectFavoriteCards({ collection: fetchedCollection, cards, isFavoriteCard }) : []),
		[fetchedCollection, cards, isFavoriteCard]
	);

	// Launching — from a favorite button or the confirm button — closes the
	// modal first, then navigates.
	// A parent's favorite for a sub-collection's card makes its record there.
	const launchCard = useCallback((cardId, card, home) => {
		setOpen(false);
		navigate(newItemPath(home?._id || collectionId, cardId));
	}, [navigate, collectionId, setOpen]);

	// A quick add lands on the same form, pre-filled by the template.
	const launchTemplate = useCallback((template, home) => {
		setOpen(false);
		launchQuickAdd(navigate, collectionId, template, home);
	}, [navigate, collectionId, setOpen]);

	// The templates this caller may launch (writable card, card in the cache),
	// ordered for the row. Whether the section shows at all is a wider
	// question: anyone who can add items sees it — empty or not — because it
	// is also the door to Manage…, where their first template gets made.
	const quickAddEntries = useMemo(
		() => (fetchedCollection ? selectQuickAddTemplates({ collection: fetchedCollection, templates: quickAdds.templates, cardsById }) : []),
		[fetchedCollection, quickAdds.templates, cardsById]
	);
	const canCreate = Boolean(fetchedCollection) && canCreateItems(fetchedCollection);
	const showQuickAdd = quickAddEntries.length > 0 || canCreate;

	// A multi add lands on its own page (several items do not fit one
	// item form). Every step's card must be writable, or the form is not
	// offered — its submit would answer 403.
	const launchForm = useCallback(form => {
		setOpen(false);
		launchMultiAdd(navigate, collectionId, form);
	}, [navigate, collectionId, setOpen]);
	const multiAddEntries = useMemo(
		() => (fetchedCollection ? selectMultiAddForms({ collection: fetchedCollection, forms: multiAdds.forms, cardsById }) : []),
		[fetchedCollection, multiAdds.forms, cardsById]
	);
	// Keeps the popups inside the modal and off the virtual list: on touch
	// devices a body-portalled menu swallows the drag and the page behind the
	// modal scrolls instead.
	const popupInModal = useCallback(trigger => trigger.parentElement || document.body, []);

	// The picker can be opened from the collections landing page, where nothing
	// else on screen says which collection the item lands in — so the modal
	// leads with the collection's identity, the way the share modal does.
	const badgeCollection = selectedHome || accessSource;

	const mergedButtonProps = { type: 'primary', ...buttonProps };
	const { onClick: triggerOnClick, disabled: triggerDisabled, ...restButtonProps } = mergedButtonProps;
	const isDisabled = triggerDisabled || !collectionId;

	const handleTriggerClick = (event) => {
		if (typeof triggerOnClick === 'function') {
			triggerOnClick(event);
		}
		if (event?.defaultPrevented || isDisabled) return;
		// A reopen starts clean: the pick from last time must not sit
		// pre-confirmed behind an enabled "Add item".
		setSelectedCardId(undefined);
		setOpen(true);
		fetchCollectionCardIds();
	};

	// Opened from outside (controlled): the same clean start and fetch a
	// trigger click does, once per opening.
	const wasOpenRef = useRef(false);
	useEffect(() => {
		if (controlled && open && !wasOpenRef.current) {
			setSelectedCardId(undefined);
			fetchCollectionCardIds();
		}
		wasOpenRef.current = open;
	// eslint-disable-next-line react-hooks/exhaustive-deps -- once per opening, not per render
	}, [controlled, open]);

	const triggerLabel = children !== undefined ? children : buttonText;
	const loaded = collectionCardIds !== null && !loadingCollection && !cardsLoading;

	return (
		<>
			{hideTrigger ? null : (
				<Button
					{...restButtonProps}
					// The items page's `N` shortcut, empty state and FAB find and
					// click this trigger.
					data-action="add-item"
					disabled={isDisabled}
					onClick={handleTriggerClick}
				>
					{triggerLabel}
				</Button>
			)}
			<Modal
				title="Add item"
				open={open}
				okText="Add item"
				okButtonProps={{ disabled: !selectedCardId }}
				onOk={() => launchCard(selectedCardId, null, selectedHome)}
				onCancel={() => setOpen(false)}
			>
				{badgeCollection ? (
					<div className="modal-identity">
						<RecordIdentity
							kicker="Collection"
							badge={(
								<RecordBadge
									kind="collection"
									record={badgeCollection}
									title={badgeCollection?.name || 'Untitled collection'}
								/>
							)}
						/>
					</div>
				) : null}
				<DialogLoadState
					loaded={loaded}
					loading={loadingCollection || cardsLoading}
					error={collectionError}
					label="this collection's cards"
					onRetry={fetchCollectionCardIds}
					skeleton={<Skeleton variant="form-field" count={3} />}
					minHeight={200}
				>
					<div className="add-item-modal">
						<section className="add-item-modal__section">
							<div className="micro-label add-item-modal__kicker">Card</div>
							<Select
								style={{ width: '100%' }}
								placeholder="Select a card"
								aria-label="Card"
								value={selectedCardId}
								onChange={setSelectedCardId}
								options={cardOptions}
								// Card options label with a ReactNode, so the shared
								// searchText matcher is what makes typeahead work here.
								showSearch
								filterOption={optionSearchFilter}
								getPopupContainer={popupInModal}
								virtual={false}
								popupClassName="card-picker-dropdown"
								optionRender={option => (
									<CardPickerOptionRow
										option={option}
										collectionId={collectionId}
										collectionFavoriteIds={collectionFavoriteIds}
										// The launch row below carries the tier branding;
										// the rows keep only the star toggle.
										showScope={false}
									/>
								)}
							/>
						</section>

						{favoriteCards.length ? (
							<section className="add-item-modal__section">
								<div className="micro-label add-item-modal__kicker">Favorites</div>
								<FavoriteCardButtons
									collectionId={collectionId}
									collection={fetchedCollection}
									cards={cards}
									isFavoriteCard={isFavoriteCard}
									onLaunch={launchCard}
									getPopupContainer={popupInModal}
								/>
							</section>
						) : null}

						{showQuickAdd ? (
							<section className="add-item-modal__section add-item-modal__quick-add">
								<div className="micro-label add-item-modal__kicker">Quick add</div>
								{quickAddEntries.length ? (
									<QuickAddButtons
										collectionId={collectionId}
										collection={fetchedCollection}
										templates={quickAdds.templates}
										cardsById={cardsById}
										onLaunch={launchTemplate}
										getPopupContainer={popupInModal}
									/>
								) : quickAdds.loading || quickAdds.loadError ? (
									<DialogLoadState
										loaded={false}
										loading={quickAdds.loading}
										error={Boolean(quickAdds.loadError)}
										label="the quick adds"
										onRetry={quickAdds.reload}
										skeleton={<Skeleton variant="line" count={2} />}
										minHeight={0}
									/>
								) : (
									<div className="launch-row launch-row--empty">
										{quickAdds.loading || quickAdds.loadError ? null : 'No quick adds yet — save one from an item form, or manage them here.'}
									</div>
								)}
								{canCreate ? (
									<Button
										type="text"
										size="small"
										className="add-item-modal__manage"
										onClick={() => setManagerOpen(true)}
									>
										Manage…
									</Button>
								) : null}
							</section>
						) : null}

						{multiAdds.loadError ? (
							<section className="add-item-modal__section add-item-modal__multi-add">
								<div className="micro-label add-item-modal__kicker">Multi add</div>
								<DialogLoadState loaded={false} error label="the multi adds" onRetry={multiAdds.reload} minHeight={0} />
							</section>
						) : multiAddEntries.length ? (
							<section className="add-item-modal__section add-item-modal__multi-add">
								<div className="micro-label add-item-modal__kicker">Multi add</div>
								<MultiAddButtons
									collectionId={collectionId}
									collection={fetchedCollection}
									forms={multiAdds.forms}
									cardsById={cardsById}
									onLaunch={launchForm}
									getPopupContainer={popupInModal}
								/>
							</section>
						) : null}

						{/* Many records at once, from a file (KPR-1899), with the card chosen above. */}
						<section className="add-item-modal__section">
							<Button
								type="text"
								size="small"
								icon={<UPLOAD />}
								className="add-item-modal__manage"
								onClick={() => {
									setOpen(false);
									navigate(importPath(selectedHome?._id || collectionId, selectedCardId));
								}}
							>
								{IMPORT_LABEL}
							</Button>
						</section>
					</div>
				</DialogLoadState>
			</Modal>
			{managerOpen ? (
				<AddItemManagerModal
					open={managerOpen}
					onClose={() => setManagerOpen(false)}
					onNavigate={() => { setManagerOpen(false); setOpen(false); }}
					collectionId={collectionId}
					collection={fetchedCollection}
					cardsById={cardsById}
					quickAdds={quickAdds}
					multiAdds={multiAdds}
				/>
			) : null}
		</>
	);
}
